/** Routes for Landing, Onboarding, Dashboard, SavedSeen, Profile — guarded for auth. */

import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router'
import { AppShell } from './components/AppShell'
import { RequireAuth, RequireOnboarded } from './auth/RequireAuth'
import Landing from './pages/Landing'
import Login from './pages/Login'
import Signup from './pages/Signup'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import Onboarding from './pages/Onboarding'
import Privacy from './pages/Privacy'
import Dashboard from './pages/Dashboard'
import SavedSeen from './pages/SavedSeen'
import Profile from './pages/Profile'
import ApplyReview from './pages/ApplyReview'

/**
 * PIVOT-174: `/__shots/*` are the dev-only routes `scripts/capture-shots.mjs`
 * screenshots for `Landing.tsx`'s three product shots. `lazy()` here, not a
 * static import, is what keeps them out of a production bundle: Vite inlines
 * `import.meta.env.DEV` to the literal `false` for a production build, so
 * this whole ternary — including the `import()` call — is dead code its
 * build-time tree-shaking removes before any chunk is ever created for it.
 * The same `import.meta.env.DEV` read gates whether `AppRoutes` below ever
 * adds the matching `<Route>` elements, so a router test can flip it at
 * runtime (real object under Vitest, unlike the inlined production build)
 * and assert the routes exist in dev and not in prod.
 */
const ShotDashboard = import.meta.env.DEV
  ? lazy(() => import('./pages/__shots/ShotDashboard'))
  : null
const ShotInterview = import.meta.env.DEV
  ? lazy(() => import('./pages/__shots/ShotInterview'))
  : null
const ShotMatch = import.meta.env.DEV ? lazy(() => import('./pages/__shots/ShotMatch')) : null

// `/onboarding` is auth-only: it must never sit behind `RequireOnboarded` too,
// or a user without a profile would be redirected from `/onboarding` to
// `/onboarding` forever (the loop this ticket exists to prevent).
function OnboardingLayout() {
  return (
    <RequireAuth>
      <AppShell onboarding />
    </RequireAuth>
  )
}

// Everything else that requires a finished profile: auth, then onboarded.
function ProtectedLayout() {
  return (
    <RequireAuth>
      <RequireOnboarded>
        <AppShell />
      </RequireOnboarded>
    </RequireAuth>
  )
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* Public and ungated on purpose: the policy has to be readable *before*
          you agree to it, and by someone who never signs up (constitution §V). */}
      <Route path="/privacy" element={<Privacy />} />
      <Route element={<OnboardingLayout />}>
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/onboarding/matcher-v2" element={<Onboarding existingUserReview />} />
      </Route>
      <Route element={<ProtectedLayout />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/saved" element={<SavedSeen />} />
        <Route path="/profile" element={<Profile />} />
        {/* `/apply/review` is the fresh unsaved artifact, copied from router
            state to browser storage; `/apply/:id` is a saved artifact,
            fetched by id so both routes survive a hard refresh (PIVOT-111). */}
        <Route path="/apply/review" element={<ApplyReview />} />
        <Route path="/apply/:id" element={<ApplyReview />} />
      </Route>
      {import.meta.env.DEV && ShotDashboard && ShotInterview && ShotMatch && (
        <>
          <Route
            path="/__shots/dashboard"
            element={
              <Suspense fallback={null}>
                <ShotDashboard />
              </Suspense>
            }
          />
          <Route
            path="/__shots/interview"
            element={
              <Suspense fallback={null}>
                <ShotInterview />
              </Suspense>
            }
          />
          <Route
            path="/__shots/match"
            element={
              <Suspense fallback={null}>
                <ShotMatch />
              </Suspense>
            }
          />
        </>
      )}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
